"use client";

import { captureClientEvent } from "@/lib/analytics/client";
import {
    authorToolProperties,
    type AuthorToolContext,
} from "@/lib/analytics/author-tool-contract";

type StartContext = Omit<AuthorToolContext, "invocationId">;

function createInvocationId(): string {
    return globalThis.crypto?.randomUUID?.() ??
        `tool-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export interface AuthorToolTracker {
    accepted: (resultType: string) => void;
    completed: (resultType: string) => void;
    failed: (errorCode: string) => void;
}

export function startAuthorToolTracking(args: StartContext): AuthorToolTracker {
    const startedAt = performance.now();
    const context: AuthorToolContext = {
        ...args,
        invocationId: createInvocationId(),
    };
    const properties = authorToolProperties(context);
    captureClientEvent("author_tool_started", properties);

    let isFinished = false;
    function finish(
        event:
            | "author_tool_accepted"
            | "author_tool_completed"
            | "author_tool_failed",
        outcome: Record<string, unknown>,
    ) {
        if (isFinished) return;
        isFinished = true;
        captureClientEvent(event, {
            ...properties,
            duration_ms: Math.max(0, Math.round(performance.now() - startedAt)),
            ...outcome,
        });
    }

    return {
        accepted(resultType) {
            finish("author_tool_accepted", { result_type: resultType });
        },
        completed(resultType) {
            finish("author_tool_completed", { result_type: resultType });
        },
        failed(errorCode) {
            finish("author_tool_failed", { error_code: errorCode });
        },
    };
}
